<template>
  <component
    :is="is ?? 'button'"
    :disabled
    :type
    :class="
      buttonVariant({
        variant,
        size,
        disabled,
      })
    "
  >
    <mep-spinner v-if="loading" :variant />
    <template v-else>
      <gv-icon v-if="leadingIcon" :name="leadingIcon" class="shrink-0 fill-[currentColor]" />
      <slot>
        <mep-type is="span" v-if="text" scheme="custom" :variant="textVariant ?? 'label-large'">{{ text }}</mep-type>
      </slot>
      <gv-icon v-if="trailingIcon" :name="trailingIcon" class="shrink-0 fill-[currentColor]" />
    </template>
  </component>
</template>

<script lang="ts" setup>
import { tw } from '@/common/helpers';
import { cva, VariantProps } from 'class-variance-authority';
import { ButtonHTMLAttributes, computed } from 'vue';
import { MepTypeProps } from './MepType/types';

const props = defineProps<{
  is?: keyof HTMLElementTagNameMap;
  text?: string;
  leadingIcon?: string;
  trailingIcon?: string;
  disabled?: boolean;
  loading?: boolean;
  variant?: ButtonVariants['variant'];
  textVariant?: MepTypeProps['variant'];
  size?: ButtonVariants['size'];
  type?: ButtonHTMLAttributes['type'];
}>();

const disabled = computed(() => props.disabled || props.loading);

const buttonVariant = cva(
  tw`inline-flex flex-row items-center justify-center gap-1 rounded-full outline-offset-2 transition-[box-shadow_background-color] disabled:cursor-default`,
  {
    variants: {
      variant: {
        primary: tw`bg-primary text-primary-on`,
        tonal: tw`bg-secondary-container text-secondary-container-on`,
        text: tw`text-primary`,
        // eslint-disable-next-line tailwindcss/no-custom-classname
        elevated: tw`elevated-shadow bg-surface-container-lowest text-primary`,
      },
      size: {
        normal: tw`h-10`,
      },
      disabled: {
        true: tw`cursor-not-allowed !bg-surface-container-lowest !text-surface-on-38`,
        false: '',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'normal',
      disabled: false,
    },
  }
);
type ButtonVariants = VariantProps<typeof buttonVariant>;
</script>

<style scoped>
button {
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
}

.elevated-shadow {
  box-shadow:
    0 1px 4px 0 var(--shadows-shadow-30, rgba(0, 0, 0, 0.3)),
    0 2px 6px 0 var(--shadows-shadow-15, rgba(0, 0, 0, 0.15));
}
.elevated-shadow:active {
  box-shadow:
    0 1px 2px 0 var(--shadows-shadow-30, rgba(0, 0, 0, 0.35)),
    0 1px 3px 0 var(--shadows-shadow-15, rgba(0, 0, 0, 0.2));
}
</style>
